import {
    createAppButton,
    createAppCompoundButton,
    createAppFlyoutAnchor,
    createAppSplitButton,
    createAppSearchBox,
    createAppToggleButton,
    createGeneratedMenuItem,
    getRibbonIconColor,
} from 'owa-command-ribbon';
import { attachmentMenuItemStyle } from 'owa-command-ribbon-styles';
import { isFeatureEnabled } from 'owa-feature-flags';
import Checkmark from 'owa-fluent-icons-svg/lib/icons/CheckmarkFilled';
import { getIsButtonDisabled } from 'owa-mail-ribbon-utils';
import { MailRibbonControlId } from 'owa-ribbon-ids';
import type { IButtonStyles } from '@fluentui/react';
import type { IKeytipProps } from '@fluentui/react/lib/Keytip';
import type { AppCompoundButtonProps } from '@1js/acui-button/lib/components/AppCompoundButton';
import type { MenuDefinition } from '@1js/acui-menu/lib/components/MenuDefinition';
import type { MenuItemControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/controls/Menu';
import type {
    RibbonButtonProps,
    RibbonFlyoutAnchorProps,
    RibbonSplitButtonProps,
} from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon';
import type { AppSearchBoxProps } from '@1js/acui-search-box/lib/components/AppSearchBox';
import {
    getDefaultRibbonStyles,
    getDefaultRibbonStylesForSelected,
} from 'owa-command-ribbon-styles/lib/util/getDefaultRibbonStyles';

/**
 * creates RibbonButtonProps object for use with MailRibbon
 * @param id Must be unique and may be used to determine enabledness and icon color
 * @param label Label displayed to the user
 * @param tooltip Hover tooltip
 * @param isPopout Is this button in a popout window? Impacts disabled logic
 * @param onExecute Function invoked when the button is executed
 * @param keytipProps Optional keytip for button
 * @param icon Optional id of an icon to be displayed. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param styles Optional styles object
 * @param overrides Optional object/array of other RibbonButtonProps props to override.
 */
export const createMailAppButton = (
    id: number | string,
    label: string,
    tooltip: string | undefined,
    isPopout: boolean,
    onExecute: () => void,
    keytipProps?: IKeytipProps,
    icon?: string,
    iconColor?: string,
    styles?: IButtonStyles,
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7006 (55,5): Parameter 'overrides' implicitly has an 'any' type.
    // @ts-expect-error
    overrides?
): RibbonButtonProps => {
    return createAppButton(
        id,
        label,
        onExecute,
        isFeatureEnabled('mon-ribbon-keytips') ? keytipProps : undefined,
        icon,
        iconColor,
        styles ?? getDefaultRibbonStyles(),
        {
            customTooltip: tooltip,
            disabled: getIsButtonDisabled(id, isPopout),
            ...overrides,
        }
    );
};

/**
 * creates RibbonButtonProps object for use with MailRibbon that has two states: selected or not.
 * Selected state will show a green checkmark icon with bolded text.
 * @param id Must be unique and may be used to determine enabledness and icon color
 * @param label Label displayed to the user
 * @param tooltip Hover tooltip
 * @param onExecute Function invoked when the button is executed
 * @param keytipProps Optional keytip for button
 * @param isSelected Whether this button should show selected state or not
 * @param overrides Optional object/array of other RibbonButtonProps props to override.
 */
export const createMailAppSelectionButton = (
    id: number | string,
    label: string,
    tooltip: string | undefined,
    onExecute: () => void,
    isSelected: boolean,
    keytipProps?: IKeytipProps,
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7006 (94,5): Parameter 'overrides' implicitly has an 'any' type.
    // @ts-expect-error
    overrides?
): RibbonButtonProps => {
    return createAppButton(
        id,
        label,
        onExecute,
        isFeatureEnabled('mon-ribbon-keytips') ? keytipProps : undefined,
        isSelected === true ? Checkmark : undefined,
        isSelected === true ? getRibbonIconColor(MailRibbonControlId.DynamicMenuButton) : undefined,
        isSelected === true ? getDefaultRibbonStylesForSelected() : getDefaultRibbonStyles(),
        {
            customTooltip: tooltip,
            ...overrides,
        }
    );
};

/**
 * creates RibbonButtonProps object for use with MailRibbon
 * @param id Must be unique and may be used to determine enabledness and icon color
 * @param label Label displayed to the user
 * @param tooltip Hover tooltip
 * @param isPopout Is this button in a popout window? Impacts disabled logic
 * @param menuDefinition Object defining the menu shown when the flyout is invoked
 * @param keytipProps Optional keytip for button
 * @param icon Optional id of an icon to be displayed. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param styles Optional styles object
 * @param overrides Optional object/array of other RibbonFlyoutAnchorProps props to override.
 */
export const createMailAppFlyoutAnchor = (
    id: number | string,
    label: string,
    tooltip: string | undefined,
    isPopout: boolean,
    menuDefinition: MenuDefinition,
    keytipProps?: IKeytipProps,
    icon?: string,
    iconColor?: string,
    styles?: IButtonStyles,
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7006 (137,5): Parameter 'overrides' implicitly has an 'any' type.
    // @ts-expect-error
    overrides?
): RibbonFlyoutAnchorProps => {
    return createAppFlyoutAnchor(
        id,
        label,
        menuDefinition,
        isFeatureEnabled('mon-ribbon-keytips') ? keytipProps : undefined,
        icon,
        iconColor,
        styles ?? getDefaultRibbonStyles(),
        {
            customTooltip: tooltip,
            disabled: getIsButtonDisabled(id, isPopout),
            ...overrides,
        }
    );
};

/**
 * creates RibbonButtonProps object for use with MailRibbon
 * @param id Must be unique and may be used to determine enabledness and icon color
 * @param label Label displayed to the user
 * @param splitButtonAriaLabel Description of the dropdown button, for screen readers
 * @param tooltip Hover tooltip
 * @param isPopout Is this button in a popout window? Impacts disabled logic
 * @param menuDefinition Object defining the menu shown when the split button chevron is invoked
 * @param onExecute Function invoked when the button is executed
 * @param keytipProps Optional keytip for button
 * @param icon Optional id of an icon to be displayed. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param styles Optional styles object
 * @param overrides Optional object/array of other RibbonSplitButtonProps props to override.
 */
export const createMailAppSplitButton = (
    id: number | string,
    label: string,
    splitButtonAriaLabel: string | undefined,
    tooltip: string | undefined,
    isPopout: boolean,
    menuDefinition: MenuDefinition,
    onExecute: () => void,
    keytipProps?: IKeytipProps,
    icon?: string,
    iconColor?: string,
    styles?: IButtonStyles,
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7006 (183,5): Parameter 'overrides' implicitly has an 'any' type.
    // @ts-expect-error
    overrides?
): RibbonSplitButtonProps => {
    return createAppSplitButton(
        id,
        label,
        menuDefinition,
        onExecute,
        isFeatureEnabled('mon-ribbon-keytips') ? keytipProps : undefined,
        icon,
        iconColor,
        styles ?? getDefaultRibbonStyles(),
        {
            customTooltip: tooltip,
            disabled: getIsButtonDisabled(id, isPopout),
            splitButtonAriaLabel,
            ...overrides,
        }
    );
};

/**
 * creates RibbonButtonProps object for use with the WAC Ribbon package. Button handles selected state.
 * @param id Unique id. To ensure uniqueness while keeping package size small, it is recommended that each ribbon maintain their own integer-based enum of unique entries, calling toString at runtime to generate string ids.
 * @param label Label displayed ot the user
 * @param tooltip Hover tooltip
 * @param isPopout Is this button in a popout window? Impacts disabled logic
 * @param onExecute Function invoked when the button is executed
 * @param toggled Whether the button should be selected
 * @param keytipProps Optional keytip for button
 * @param icon Optional id of an icon to be displayed. Typically ControlIcons, MailIcons, or CalendarIcons. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param styles Optional style object.
 * @param overrides Optional object/array of other RibbonButtonProps props to override.
 */
export const createMailAppToggleButton = (
    id: number | string,
    label: string,
    tooltip: string | undefined,
    isPopout: boolean,
    onExecute: () => void,
    toggled: boolean,
    keytipProps?: IKeytipProps,
    icon?: string,
    iconColor?: string,
    styles?: IButtonStyles,
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7006 (230,5): Parameter 'overrides' implicitly has an 'any' type.
    // @ts-expect-error
    overrides?
): RibbonButtonProps => {
    return createAppToggleButton(
        id,
        label,
        onExecute,
        toggled,
        isFeatureEnabled('mon-ribbon-keytips') ? keytipProps : undefined,
        icon,
        iconColor,
        styles ?? getDefaultRibbonStyles(),
        {
            customTooltip: tooltip,
            disabled: getIsButtonDisabled(id, isPopout),
            ...overrides,
        }
    );
};

/**
 * creates MenuItemControlProps object for use with the WAC Ribbon package. Used for generated menu items (i.e. from a custom list of categories)
 * @param id Unique id. To ensure uniqueness while keeping package size small, it is recommended that each ribbon maintain their own integer-based enum of unique entries, calling toString at runtime to generate string ids.
 * @param label Label displayed ot the user
 * @param tooltip Hover tooltip
 * @param onExecute Function invoked when the button is executed
 * @param keytipProps Optional keytip for button
 * @param isSelected Optional boolean. If provided, the icon will be displayed only if true.
 * @param icon Optional id of an icon to be displayed. It is the client's responsibility to ensure icon has been registered and is available at run-time. Defaults to checkmark if not provide and isSelected is true.
 * @param iconColor Optional icon foreground color override. Defaults to green if color not provided and isSelected is true.
 * @param styles Optional styles object.
 * @param customTooltip Optional hover tooltip
 * @param overrides Optional object/array of other MenuItemControlProps props to override.
 */
export const createMailGeneratedMenuItem = (
    id: number | string,
    label: string,
    tooltip: string | undefined,
    onExecute: () => void,
    keytipProps?: IKeytipProps,
    isSelected?: boolean,
    icon?: string,
    iconColor?: string,
    styles?: IButtonStyles,
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7006 (276,5): Parameter 'overrides' implicitly has an 'any' type.
    // @ts-expect-error
    overrides?
): MenuItemControlProps => {
    return createGeneratedMenuItem(
        id,
        label,
        onExecute,
        isFeatureEnabled('mon-ribbon-keytips') ? keytipProps : undefined,
        icon ?? (isSelected === true ? Checkmark : undefined),
        iconColor ??
            (isSelected === true
                ? getRibbonIconColor(MailRibbonControlId.DynamicMenuButton)
                : undefined),
        styles ??
            (isSelected === true ? getDefaultRibbonStylesForSelected() : getDefaultRibbonStyles()),
        tooltip,
        overrides
    );
};

/**
 * creates RibbonFlyoutAnchorProps object for use with MailRibbon
 * @param id Must be unique and may be used to determine enabledness and icon color
 * @param label Label displayed to the user
 * @param tooltip Hover tooltip
 * @param menuDefinition Object defining the menu shown when the flyout is invoked
 * @param keytipProps Optional keytip for button
 * @param isSelected Optional boolean. If provided, the icon will be displayed only if true.
 * @param icon Optional id of an icon to be displayed. It is the client's responsibility to ensure icon has been registered and is available at run-time. Defaults to checkmark if not provide and isSelected is true.
 * @param iconColor Optional icon foreground color override. Defaults to green if color not provided and isSelected is true.
 * @param overrides Optional object/array of other MenuItemControlProps props to override.
 */
export const createMailGeneratedMenuFlyoutAnchor = (
    id: number | string,
    label: string,
    tooltip: string | undefined,
    menuDefinition: MenuDefinition,
    keytipProps?: IKeytipProps,
    isSelected?: boolean,
    icon?: string,
    iconColor?: string,
    styles?: IButtonStyles,
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7006 (319,5): Parameter 'overrides' implicitly has an 'any' type.
    // @ts-expect-error
    overrides?
): RibbonFlyoutAnchorProps => {
    return createAppFlyoutAnchor(
        id,
        label,
        menuDefinition,
        isFeatureEnabled('mon-ribbon-keytips') ? keytipProps : undefined,
        icon ?? (isSelected === true ? Checkmark : undefined),
        iconColor ??
            (isSelected === true
                ? getRibbonIconColor(MailRibbonControlId.DynamicMenuButton)
                : undefined),
        styles ??
            (isSelected === true ? getDefaultRibbonStylesForSelected() : getDefaultRibbonStyles()),
        {
            customTooltip: tooltip,
            ...overrides,
        }
    );
};

/**
 * creates AppCompoundButtonProps object for use with the WAC Ribbon package
 * @param id Unique id. To ensure uniqueness while keeping package size small, it is recommended that each ribbon maintain their own integer-based enum of unique entries, calling toString at runtime to generate string ids.
 * @param label Label displayed to the user
 * @param tooltip Hover tooltip
 * @param description Secondary information displayed to the user
 * @param onExecute Function invoked when the button is executed
 * @param icon Optional id of an icon to be displayed. Typically ControlIcons, MailIcons, or CalendarIcons. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param styles Optional styles object.
 * @param overrides Optional object/array of other AppCompoundButtonProps props to override.
 */
export const createMailAppCompoundMenuButton = (
    id: number | string,
    label: string,
    tooltip: string | undefined,
    description: string,
    onExecute: () => void,
    icon?: string,
    iconColor?: string,
    styles?: IButtonStyles,
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7006 (363,5): Parameter 'overrides' implicitly has an 'any' type.
    // @ts-expect-error
    overrides?
): AppCompoundButtonProps => {
    return createAppCompoundButton(
        id,
        label,
        description,
        onExecute,
        icon,
        iconColor,
        styles ?? attachmentMenuItemStyle,
        tooltip,
        overrides
    );
};

/**
 * creates AppSearchBoxProps object for use with the WAC Ribbon package
 * @param id Unique id. To ensure uniqueness while keeping package size small, it is recommended that each ribbon maintain their own integer-based enum of unique entries, calling toString at runtime to generate string ids.
 * @param label Placeholder text for the search box
 * @param onChangeCallback Callback when the searchbox contents change. Empty box returns "undefined" newValue
 * @param keytipProps Optional keytip for search box
 * @param icon Optional id of an icon to be displayed. Typically ControlIcons, MailIcons, or CalendarIcons. It is the client's responsibility to ensure icon has been registered and is available at run-time.
 * @param iconColor Optional icon foreground color override
 * @param overrides Optional object/array of other AppSearchBoxProps props to override.
 */
export const createMailAppSearchBox = (
    id: number | string,
    label: string,
    onChangeCallback: (event?: React.ChangeEvent<HTMLInputElement>, newValue?: string) => void,
    keytipProps?: IKeytipProps,
    icon?: string,
    iconColor?: string,
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7006 (398,5): Parameter 'overrides' implicitly has an 'any' type.
    // @ts-expect-error
    overrides?
): AppSearchBoxProps => {
    return createAppSearchBox(
        id,
        label,
        onChangeCallback,
        isFeatureEnabled('mon-ribbon-keytips') ? keytipProps : undefined,
        icon,
        iconColor,
        {
            autoComplete: 'off',
            ...overrides,
        }
    );
};
